import { ORDER_EVENT_STATUS_REJECTED, ORDER_EVENT_STATUS_VOIDED } from '@klear/constants/order-event-status'
import { useMemo } from 'react'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { ChartContainer, ChartTooltip } from '@klear/ui/components/chart'
import type { ChartConfig } from '@klear/ui/components/chart'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import type { CurrencyCode } from '@klear/constants/currency'
import { moneyFormatterFor } from '@klear/money/currency'
import { KlearDate } from '@klear/date'
import type { InvoiceListItem } from '@klear/invoice/invoice-list.wire'
import type { Payable } from '@klear/payable/payable'
import { payableIsEarlypay, payableIsFee } from '@klear/payable/list-display'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { Money } from '@klear/money'

import toNumber from 'lodash/toNumber'

type PayableInvoice = Pick<InvoiceListItem, 'amount' | 'effectiveDate'>

const MONTHS_BACK = 3
const MONTHS_FORWARD = 6

type MonthBucket = {
  key: string
  label: string
  earlypay: number
  regular: number
}

type Aggregation = {
  totalPayables: number
  totalEarlypay: number
  totalFees12mo: number
  costOfKlearPct: string | null
  chartData: MonthBucket[]
}

function payableAmountMinor(payable: Payable): number {
  return toNumber(payable.amount.amount.toString())
}

function invoiceAmountMinor(invoice: PayableInvoice): number {
  return toNumber(invoice.amount.amount.toString())
}

function buildEmptyBuckets(now: KlearDate): MonthBucket[] {
  const buckets: MonthBucket[] = []
  for (let offset = -MONTHS_BACK; offset <= MONTHS_FORWARD; offset++) {
    const date = now.add(offset, 'month').startOf('month')
    buckets.push({
      key: date.toString('YYYY-MM'),
      label: date.toString('MMM YY'),
      earlypay: 0,
      regular: 0
    })
  }
  return buckets
}

const SKIPPED_PAYABLE_STATUSES = new Set<string>([ORDER_EVENT_STATUS_VOIDED, ORDER_EVENT_STATUS_REJECTED])

function aggregate(payables: Payable[], invoices: PayableInvoice[], selectedCurrency: CurrencyCode): Aggregation {
  const now = KlearDate.today()
  const twelveMonthsAgo = now.subtract(12, 'month')
  const quarterStart = now.startOf('quarter')
  const quarterEnd = quarterStart.add(3, 'month')

  let totalPayables = 0
  let totalEarlypay = 0
  let totalFees12mo = 0
  let feesThisQuarter = 0
  let invoiceFaceValueThisQuarter = 0

  const buckets = buildEmptyBuckets(now)
  const bucketByKey = new Map(buckets.map((b) => [b.key, b]))
  const earliestBucketDate = now.add(-MONTHS_BACK, 'month').startOf('month')
  const latestBucketDate = now.add(MONTHS_FORWARD, 'month').endOf('month')

  for (const payable of payables) {
    if (SKIPPED_PAYABLE_STATUSES.has(payable.status)) continue

    const payableMoney = payable.amount
    if (!payableMoney || payableMoney.currency !== selectedCurrency) {
      continue
    }

    const amount = payableAmountMinor(payable)
    const date = payable.effectiveDate
    const earlypay = payableIsEarlypay(payable)
    const fee = payableIsFee(payable)

    if (!fee) {
      totalPayables += amount
      if (earlypay) totalEarlypay += amount
    }

    if (fee) {
      if (date.isAfter(twelveMonthsAgo)) totalFees12mo += amount
      if (date.isAfter(quarterStart) && date.isBefore(quarterEnd)) {
        feesThisQuarter += amount
      }
    }

    if (!fee) {
      if (date.isAfter(earliestBucketDate) && date.isBefore(latestBucketDate)) {
        const bucket = bucketByKey.get(date.startOf('month').toString('YYYY-MM'))
        if (bucket) {
          if (earlypay) bucket.earlypay += amount
          else bucket.regular += amount
        }
      }
    }
  }

  for (const invoice of invoices) {
    const invoiceMoney = invoice.amount
    if (!invoiceMoney || invoiceMoney.currency !== selectedCurrency) {
      continue
    }

    const issued = invoice.effectiveDate
    if (issued.isAfter(quarterStart) && issued.isBefore(quarterEnd)) {
      invoiceFaceValueThisQuarter += invoiceAmountMinor(invoice)
    }
  }

  const costOfKlearPct =
    invoiceFaceValueThisQuarter > 0 ? `${((feesThisQuarter / invoiceFaceValueThisQuarter) * 100).toFixed(1)}%` : null

  return { totalPayables, totalEarlypay, totalFees12mo, costOfKlearPct, chartData: buckets }
}

interface MetricCardProps {
  title: string
  value: string
  hint?: string
}

function MetricCard({ title, value, hint }: MetricCardProps) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {hint ? <p className="text-xs text-muted-foreground mt-1">{hint}</p> : null}
      </CardContent>
    </Card>
  )
}

const EARLYPAY_COLOR = '#0066FF'
const REGULAR_COLOR = '#94A3B8'

const chartConfig: ChartConfig = {
  earlypay: { label: 'Earlypay', color: EARLYPAY_COLOR },
  regular: { label: 'Regular', color: REGULAR_COLOR }
}

interface PayablesDueByMonthChartProps {
  data: MonthBucket[]
  currency: CurrencyCode
}

function PayablesDueByMonthChart({ data, currency }: PayablesDueByMonthChartProps) {
  const formatMoney = moneyFormatterFor(currency)
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Payables due by month</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[260px] w-full">
          <BarChart data={data} margin={{ left: 45, right: 15, top: 10, bottom: 10 }}>
            <CartesianGrid vertical={false} />
            <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
            <YAxis
              width={60}
              tickFormatter={(value: number) => formatMoney(value, { notation: 'compact' })}
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 12 }}
            />
            <ChartTooltip
              cursor={{ fill: 'rgba(0,0,0,0.04)' }}
              wrapperStyle={{ outline: 'none' }}
              formatter={(value, name) => [formatMoney(value as number), name === 'earlypay' ? 'Earlypay' : 'Regular']}
            />
            <Bar dataKey="earlypay" stackId="payables" fill={EARLYPAY_COLOR} />
            <Bar dataKey="regular" stackId="payables" fill={REGULAR_COLOR} />
          </BarChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

interface PayablesInsightsProps {
  payables: Payable[]
  invoices: PayableInvoice[]
}

export function PayablesInsights({ payables, invoices }: PayablesInsightsProps) {
  const payableMonies = useMemo(
    () => payables.map((payable) => payable.amount).filter((money): money is Money => money != null),
    [payables]
  )

  const invoiceMonies = useMemo(
    () => invoices.map((invoice) => invoice.amount).filter((money): money is Money => money != null),
    [invoices]
  )

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    payableMonies,
    invoiceMonies
  ])

  const { totalPayables, totalEarlypay, totalFees12mo, costOfKlearPct, chartData } = useMemo(
    () => aggregate(payables, invoices, selectedCurrency),
    [payables, invoices, selectedCurrency]
  )
  const formatMoney = moneyFormatterFor(selectedCurrency)

  return (
    <section className="flex flex-col gap-6">
      {showCurrencySelector && (
        <div className="flex items-center justify-end gap-3">
          <span className="text-sm text-muted-foreground">Insights currency</span>
          <CurrencyReportSelector
            value={selectedCurrency}
            onChange={setSelectedCurrency}
            currencies={availableCurrencies}
          />
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <MetricCard title="Total Payables" value={formatMoney(totalPayables, { notation: 'compact' })} />
        <MetricCard title="Total Earlypay" value={formatMoney(totalEarlypay, { notation: 'compact' })} />
        <MetricCard
          title="Total Fees"
          value={formatMoney(totalFees12mo, { notation: 'compact' })}
          hint="Last 12 months"
        />
        <MetricCard title="Cost of Klear" value={costOfKlearPct ?? '—'} hint="Fees / new receivables (this quarter)" />
      </div>
      <PayablesDueByMonthChart data={chartData} currency={selectedCurrency} />
    </section>
  )
}
